import { createUseStyles } from "react-jss";
import {
    controlBackgroundColor,
    alpha,
    foregroundColor,
    darken,
    getSpacing,
} from "../../../theme/Mixins";
import * as ChoiceTokens from "../../../theme/choiceThemeTokens";
import { Theme } from "../../../types/Theme";

const isModernTheme = (theme: Theme) => theme.UseModernTheme === true;

export const modernNpsCellBorderWidth = (_theme: Theme): number =>
    ChoiceTokens.MODERN_ACTIVE_BORDER_WIDTH_PX;

/** Extra selection ring (px) via box-shadow so border width stays at theme.BorderWidth. */
const modernNpsSelectedRingSpread = (theme: Theme): number =>
    Math.max(modernNpsCellBorderWidth(theme) - theme.BorderWidth, 1);

const modernNpsSelectedRingShadow = (theme: Theme): string =>
    `0 0 0 ${modernNpsSelectedRingSpread(theme)}px ${theme.PrimaryColor}`;

/** NPS keyboard focus halo; selected cells use primary to match the selection ring. */
const resolveModernNpsFocusRingColor = (theme: Theme, selected?: boolean): string =>
    selected ? theme.PrimaryColor : (theme.NeutralDarkColor ?? theme.ForegroundColorDark);

/**
 * Keyboard focus for modern NPS cells. Legacy uses JS-driven choiceActive instead — do not
 * apply undefined box-shadow on focus for legacy (clears the default border).
 */
const modernNpsFocusPseudoStyles = {
    "&:focus-visible": {
        boxShadow: ({ theme, selected }: { theme: Theme; selected?: boolean }) => {
            if (!isModernTheme(theme)) {
                return undefined;
            }
            const gap = theme.WhiteColor ?? theme.ControlBackground;
            const ring = resolveModernNpsFocusRingColor(theme, selected);
            const selection = selected ? `${modernNpsSelectedRingShadow(theme)}, ` : "";
            return `${selection}0 0 0 2px ${gap}, 0 0 0 4px ${ring}`;
        },
    },
};

export default createUseStyles({
    choice: {
        background: ({ theme }: { theme: Theme }) =>
            alpha(theme.ControlBackground, theme.ControlAlpha),
        border: ({ theme }: { theme: Theme }) => [
            [theme.BorderWidth, "solid", theme.ControlBorderColor],
        ],
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        color: ({ theme }: { theme: Theme }) =>
            foregroundColor(theme, controlBackgroundColor(theme)),
        composes: "choice radio-button",
        cursor: "pointer",
        flex: 1,
        outline: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? "2px solid transparent" : "none",
        position: "relative",
        textAlign: "center",
        transition: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? "background 200ms, border-color 200ms, color 200ms, box-shadow 200ms"
                : "background 200ms, border-color 200ms, color 200ms",
        boxSizing: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? ("border-box" as const) : undefined,
        margin: 3,

        "&:before": {
            content: "''",
            float: "left",
            paddingTop: "100%",
        },
    },
    /** Modern keyboard focus ring only — keeps legacy free of :focus-visible box-shadow. */
    choiceModernFocus: {
        ...modernNpsFocusPseudoStyles,
    },
    dynamicChoice: {
        width: "100%",
        margin: ({ theme }: { theme: Theme }) => [
            [
                getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents) / 2,
                0,
            ],
        ],
        padding: [[14.5, 0]],

        "&:before": {
            paddingTop: "0",
            height: 29,
            width: "100%",
        },
    },
    choiceSelected: {
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? alpha(theme.ControlBackground, theme.ControlAlpha)
                : theme.PrimaryColor,
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? theme.PrimaryColor
                : darken(theme.PrimaryColor, theme.ActiveHighlightValue),
        boxShadow: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? modernNpsSelectedRingShadow(theme) : undefined,
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? foregroundColor(theme, controlBackgroundColor(theme))
                : foregroundColor(theme, theme.PrimaryColor),
        composes: "choice--selected",
    },
    choiceHover: {
        composes: "choice--hover",
        /** Modern only; legacy uses choiceActive (NetPromoterItemRenderer classnames). */
        background: ({ theme }: { theme: Theme }) => {
            if (!isModernTheme(theme)) {
                return undefined;
            }
            return alpha(theme.NeutralDarkColor, 0.08);
        },
    },
    choiceActive: {
        composes: "choice--active",
        background: ({ theme, selected }: { theme: Theme; selected: boolean }) => {
            if (isModernTheme(theme)) {
                return undefined;
            }
            return selected
                ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                : darken(theme.ControlBackground, theme.ActiveBackgroundValue);
        },
        color: ({ theme, selected }: { theme: Theme; selected: boolean }) => {
            if (isModernTheme(theme)) {
                return undefined;
            }
            return selected
                ? foregroundColor(theme, darken(theme.PrimaryColor, theme.ActiveHighlightValue))
                : foregroundColor(
                      theme,
                      darken(theme.ControlBackground, theme.ActiveBackgroundValue),
                  );
        },
    },
    choiceDisabled: {
        composes: "choice--disabled",
        background: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme)
                ? alpha(theme.ControlBackground, theme.ControlAlpha)
                : undefined,
        borderColor: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? alpha(theme.NeutralDarkColor, 0.35) : undefined,
        boxShadow: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? "none" : undefined),
        color: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? alpha(theme.NeutralDarkColor, 0.35) : undefined,
        cursor: ({ theme }: { theme: Theme }) => (isModernTheme(theme) ? "default" : undefined),
        pointerEvents: ({ theme }: { theme: Theme }) =>
            isModernTheme(theme) ? ("none" as const) : undefined,
    },
    content: {
        alignItems: "center",
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        left: 0,
        position: "absolute",
        right: 0,
        top: 0,
    },
} as any);

